import { useCallback, useEffect, useMemo, useState } from "react";
import { ControllerListarClientesModulo, ControllerCambiarEstadoCliente } from "../../Service/Service.cliente";
import { GetTokenDecoded } from "../../utils/storeUsuario";
import usePermissions from "../../hook/usePermissions";
import ModalCliente from "./ModalCliente";
import DataTable from "../UI/DataTable/DataTable";
import { Celda, CeldaDoble, columnasVisibles } from "../UI/DataTable/piezas";
import { ChipEstado, ChipCategoria } from "../UI/Chip";
import Boton from "../UI/Boton";
import MenuAcciones from "../UI/MenuAcciones";
import Modal from "../UI/Modal/Modal";
import { useToast } from "../UI/Aviso";
import { IconoMas, IconoEditar, IconoEliminar, IconoCheck } from "../UI/Iconos";

// CLIENTES-MODULO-01: grid del módulo /clientes. `estado='A'` (Activo) es
// seleccionable en facturación; cualquier otro = Inactivo (se conserva para el
// historial de facturas, no aparece en selectores de factura nueva).
const esActivo = (estado) => String(estado ?? "A") === "A";

// SISTEMA-DE-DISENO · pantalla migrada al kit (4ª).
//
// MIGRAR NO ES REDISEÑAR: `ControllerListarClientesModulo` se sigue llamando
// igual (una vez, con la misma clínica y los mismos filtros), el permiso
// `clients.manage` sigue mandando y no hay borrado físico -- sólo inactivar y
// reactivar, como antes.
//
// Lo que cambia:
//   · 8 columnas -> 5. La identificación y su tipo se leen con el nombre; el
//     teléfono, con el correo.
//   · "Estado" DESAPARECE como columna cuando el filtro la fija (el panel abre
//     en "Activos"), y reaparece con "Todos". Es la regla de columnas del
//     sistema aplicada donde de verdad se nota.
//   · La acción principal ("Editar") queda visible; inactivar/reactivar pasa al
//     menú, en rojo cuando corresponde.
//   · Los dos `Swal` se van al Modal y a los toasts del kit.
//   · La tabla gana estados de vacío y de ERROR, que no tenía.
function GridClientes({ data }) {
    const { filters, onTotal } = data;
    const { hasPermission } = usePermissions();
    const { mostrar } = useToast();
    const puedeEditar = hasPermission("clients.manage");

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [actingId, setActingId] = useState(null);
    const [confirmarEstado, setConfirmarEstado] = useState(null);

    // La consulta trae TODOS los clientes de la clínica de una vez (así era y
    // así sigue siendo: una sola llamada). La paginación es local.
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(15);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        const idclinica = GetTokenDecoded()?.idclinica;
        const r = await ControllerListarClientesModulo(idclinica, {
            search: filters?.search ?? "",
            estado: filters?.estado ?? "A",
        });
        if (r && r.success) {
            setRows(r.data || []);
            if (onTotal) onTotal((r.data || []).length);
        } else {
            setRows([]);
            if (onTotal) onTotal(0);
            setError(r?.message || "No se pudo obtener el listado de clientes.");
        }
        setLoading(false);
    }, [filters, onTotal]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Al cambiar el filtro se vuelve a la primera página: la anterior puede ya
    // no existir en el resultado filtrado.
    useEffect(() => {
        setPage(1);
    }, [filters]);

    const abrirNuevo = () => {
        setEditing(null);
        setModalOpen(true);
    };
    const abrirEditar = (record) => {
        setEditing(record);
        setModalOpen(true);
    };
    const cerrarModal = (recargar) => {
        setModalOpen(false);
        setEditing(null);
        if (recargar) fetchData();
    };

    const aplicarCambioEstado = async () => {
        const record = confirmarEstado;
        if (!record) return;
        const activar = !esActivo(record.estado);
        setConfirmarEstado(null);
        setActingId(record.idcliente);
        const r = await ControllerCambiarEstadoCliente(record.idcliente, activar ? "A" : "I");
        setActingId(null);
        if (r && r.success) {
            mostrar({ texto: activar ? "Cliente reactivado" : "Cliente inactivado" });
            fetchData();
        } else {
            mostrar({ tipo: "error", texto: r?.message || "No se pudo cambiar el estado del cliente." });
        }
    };

    const columnas = [
        {
            key: "nombre",
            titulo: "Nombre / Razón social",
            render: (r) => (
                <CeldaDoble
                    principal={r.nombre}
                    secundario={`${r.tipo_documentos?.tipo || "Doc."} ${r.identificacion || "—"}`}
                    monoSecundario
                />
            ),
        },
        {
            key: "persona",
            titulo: "Persona",
            ancho: 130,
            render: (r) => <ChipCategoria>{Number(r.es_juridica) === 1 ? "Jurídica" : "Natural"}</ChipCategoria>,
        },
        {
            key: "contacto",
            titulo: "Contacto",
            ancho: 240,
            render: (r) => <CeldaDoble principal={r.correo} secundario={r.celular || r.telefono} />,
        },
        {
            // Regla de columnas: si el filtro fija el estado, esta columna no
            // aporta nada y desaparece; con "Todos" vuelve.
            key: "estado",
            titulo: "Estado",
            ancho: 130,
            fijadaPor: "estado",
            render: (r) => (
                <ChipEstado tono={esActivo(r.estado) ? "hecho" : "pendiente"}>
                    {esActivo(r.estado) ? "Activo" : "Inactivo"}
                </ChipEstado>
            ),
        },
        {
            key: "accion",
            titulo: "",
            ancho: 150,
            alinear: "derecha",
            render: (r) => {
                if (!puedeEditar) return null;
                const activo = esActivo(r.estado);
                return (
                    <span className="ac-celda-accion">
                        <Boton
                            tamano="sm"
                            icono={IconoEditar}
                            onClick={(e) => {
                                e.stopPropagation();
                                abrirEditar(r);
                            }}
                            data-testid={`cliente-editar-${r.idcliente}`}
                        >
                            Editar
                        </Boton>
                        <MenuAcciones
                            titulo={`Más acciones de ${r.nombre}`}
                            acciones={[
                                {
                                    key: "estado",
                                    label: activo ? "Inactivar" : "Reactivar",
                                    icono: activo ? IconoEliminar : IconoCheck,
                                    peligro: activo,
                                    disabled: actingId === r.idcliente,
                                    onClick: () => setConfirmarEstado(r),
                                },
                            ]}
                        />
                    </span>
                );
            },
        },
    ];

    // "all" significa que el filtro NO fija un estado: entonces la columna sí
    // aporta y se muestra.
    const filtrosDeColumna = { estado: filters?.estado === "all" ? null : filters?.estado ?? "A" };
    const columnasFinales = columnasVisibles(columnas, filtrosDeColumna);

    const pagina = useMemo(() => rows.slice((page - 1) * pageSize, page * pageSize), [rows, page, pageSize]);
    const hayFiltros = Boolean(filters?.search) || (filters?.estado && filters.estado !== "A");
    const activandoAhora = confirmarEstado && !esActivo(confirmarEstado.estado);

    return (
        <>
            {puedeEditar && (
                <div className="mb-2 d-flex justify-content-end">
                    <Boton variante="primario" icono={IconoMas} onClick={abrirNuevo} data-testid="cliente-nuevo">
                        Nuevo cliente
                    </Boton>
                </div>
            )}

            <DataTable
                columnas={columnasFinales}
                filas={pagina}
                filaKey="idcliente"
                cargando={loading}
                error={error}
                onReintentar={fetchData}
                etiquetaTabla="Clientes de facturación"
                hayFiltrosAplicados={hayFiltros}
                textoVacioSinFiltros="Todavía no hay clientes de facturación registrados."
                onLimpiarFiltros={undefined}
                paginacion={{
                    page,
                    pageSize,
                    total: rows.length,
                    tamanos: [15, 25, 50, 100],
                    onCambio: (p, ps) => {
                        setPage(p);
                        setPageSize(ps);
                    },
                }}
            />

            {/* Inactivar es la acción con consecuencias: se confirma diciendo
                exactamente qué implica, como hacía el Swal. */}
            <Modal
                abierto={!!confirmarEstado}
                titulo={activandoAhora ? "¿Reactivar este cliente?" : "¿Inactivar este cliente?"}
                ancho="confirmacion"
                destructivo={!activandoAhora}
                onCerrar={() => setConfirmarEstado(null)}
                secundario={{ label: "Cancelar" }}
                primario={{
                    label: activandoAhora ? "Reactivar" : "Inactivar",
                    onClick: aplicarCambioEstado,
                    cargando: actingId === confirmarEstado?.idcliente,
                }}
            >
                <p data-testid="cliente-confirmacion">
                    {activandoAhora
                        ? `${confirmarEstado?.nombre} volverá a estar disponible para nuevas facturas.`
                        : `${confirmarEstado?.nombre} dejará de aparecer en el selector de facturación. Sus facturas históricas se conservan.`}
                </p>
            </Modal>

            <ModalCliente data={{ show: modalOpen, handleClose: cerrarModal, cliente: editing }} />
        </>
    );
}

export default GridClientes;
